/**
 * useSegmentConversation Hook
 * 
 * React hook for managing segment conversation state.
 * Handles message sending, undo, and filter updates.
 */

import { useState, useCallback } from 'react';
import { segmentsApi } from '../api';
import { Segment, ConversationTurn, Suggestion } from '../types';

interface ConversationState {
    segmentId: string | null;
    filter: Record<string, unknown>;
    history: ConversationTurn[];
    summary: string;
    count: number;
    undoIndex: number;
    suggestions: Suggestion[];
}

interface UseSegmentConversationReturn {
    // State
    state: ConversationState;
    loading: boolean;
    error: string | null;

    // Clarification
    needsClarification: boolean;
    clarificationQuestion: string | null;
    clarificationOptions: Suggestion[];

    // Actions
    sendMessage: (message: string) => Promise<void>;
    selectClarification: (option: Suggestion) => Promise<void>;
    undo: () => Promise<void>;
    removeFilter: (field: string) => Promise<void>;
    reset: () => void;
    loadSegment: (id: string, brandId: string) => Promise<void>;
    saveSegment: (name: string) => Promise<Segment | null>;
}

export function useSegmentConversation(brandId: string): UseSegmentConversationReturn {
    const [state, setState] = useState<ConversationState>({
        segmentId: null,
        filter: {},
        history: [],
        summary: '',
        count: 0,
        undoIndex: 0,
        suggestions: [],
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [needsClarification, setNeedsClarification] = useState(false);
    const [clarificationQuestion, setClarificationQuestion] = useState<string | null>(null);
    const [clarificationOptions, setClarificationOptions] = useState<Suggestion[]>([]);

    /**
     * Send a message to the conversation
     */
    const sendMessage = useCallback(async (message: string) => {
        setLoading(true);
        setError(null);
        setNeedsClarification(false);

        try {
            const result = await segmentsApi.converse({
                brandId,
                segmentId: state.segmentId || undefined,
                message,
                filter: state.filter,
                count: state.count,
                summary: state.summary,
            });

            if (result.success && result.data) {
                const data = result.data;

                // Check for clarification
                if (data.needsClarification) {
                    setNeedsClarification(true);
                    setClarificationQuestion(data.clarificationQuestion || null);
                    setClarificationOptions(data.clarificationOptions || []);
                    return;
                }

                // Update state with response
                setState(prev => ({
                    segmentId: data.segmentId || prev.segmentId,
                    filter: data.filter || prev.filter,
                    history: data.conversationHistory || prev.history,
                    summary: data.conversationSummary || prev.summary,
                    count: data.lastKnownCount ?? prev.count,
                    undoIndex: data.currentUndoIndex ?? 0,
                    suggestions: data.suggestions || [],
                }));
            } else {
                setError(result.error || 'Error en la conversación');
            }
        } catch (err) {
            setError('Error de conexión');
            console.error('Conversation error:', err);
        } finally {
            setLoading(false);
        }
    }, [brandId, state]);

    /**
     * Select a clarification option
     */
    const selectClarification = useCallback(async (option: Suggestion) => {
        setNeedsClarification(false);
        setClarificationQuestion(null);
        setClarificationOptions([]);
        await sendMessage(option.promptToSend);
    }, [sendMessage]);

    /**
     * Undo last action
     */
    const undo = useCallback(async () => {
        if (!state.segmentId) return;

        setLoading(true);
        setError(null);

        try {
            const result = await segmentsApi.undo(state.segmentId, brandId);

            if (result.success && result.data) {
                setState(prev => ({
                    ...prev,
                    filter: result.data.restoredFilter || {},
                    count: result.data.restoredCount ?? prev.count,
                    undoIndex: result.data.newUndoIndex ?? prev.undoIndex + 1,
                }));
            } else {
                setError(result.error || 'No hay más acciones para deshacer');
            }
        } catch (err) {
            setError('Error al deshacer');
            console.error('Undo error:', err);
        } finally {
            setLoading(false);
        }
    }, [brandId, state.segmentId]);

    /**
     * Remueve un filtro específico (click en X de un chip).
     * Mantiene el resto del state y agrega un turno del bot.
     */
    const removeFilter = useCallback(
        async (field: string) => {
            if (!state.segmentId) return;
            setLoading(true);
            setError(null);
            try {
                const result = await segmentsApi.removeFilter(
                    state.segmentId,
                    brandId,
                    field,
                );
                if (result.success && result.data) {
                    const data = result.data;
                    setState((prev) => ({
                        segmentId: data.segmentId || prev.segmentId,
                        filter: data.filter || {},
                        history: data.conversationHistory || prev.history,
                        summary: data.conversationSummary ?? prev.summary,
                        count: data.lastKnownCount ?? prev.count,
                        undoIndex: data.currentUndoIndex ?? 0,
                        suggestions: data.suggestions || [],
                    }));
                } else {
                    setError(result.error || "No se pudo quitar el filtro");
                }
            } catch (err) {
                setError("Error de conexión");
                console.error("Remove filter error:", err);
            } finally {
                setLoading(false);
            }
        },
        [brandId, state.segmentId],
    );

    /**
     * Reset conversation to initial state
     */
    const reset = useCallback(() => {
        setState({
            segmentId: null,
            filter: {},
            history: [],
            summary: '',
            count: 0,
            undoIndex: 0,
            suggestions: [],
        });
        setNeedsClarification(false);
        setClarificationQuestion(null);
        setClarificationOptions([]);
        setError(null);
    }, []);

    /**
     * Load an existing segment
     */
    const loadSegment = useCallback(async (id: string, brandId: string) => {
        setLoading(true);
        setError(null);

        try {
            const result = await segmentsApi.get(id, brandId);

            if (result.success && result.data) {
                const segment = result.data;
                setState({
                    segmentId: segment.id,
                    filter: segment.filter,
                    history: segment.conversationHistory,
                    summary: segment.conversationSummary,
                    count: segment.lastKnownCount ?? 0,
                    undoIndex: segment.currentUndoIndex,
                    suggestions: [],
                });
            } else {
                setError('Segmento no encontrado');
            }
        } catch (err) {
            setError('Error al cargar segmento');
            console.error('Load segment error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Save segment with name
     */
    const saveSegment = useCallback(async (name: string): Promise<Segment | null> => {
        if (!state.segmentId) return null;

        setLoading(true);
        setError(null);

        try {
            const result = await segmentsApi.save(state.segmentId, brandId, name);

            if (result.success && result.data) {
                return result.data;
            } else {
                setError(result.error || 'Error al guardar');
                return null;
            }
        } catch (err) {
            setError('Error al guardar');
            console.error('Save segment error:', err);
            return null;
        } finally {
            setLoading(false);
        }
    }, [brandId, state.segmentId]);

    return {
        state,
        loading,
        error,
        needsClarification,
        clarificationQuestion,
        clarificationOptions,
        sendMessage,
        selectClarification,
        undo,
        removeFilter,
        reset,
        loadSegment,
        saveSegment,
    };
}

export default useSegmentConversation;
